import React from 'react';
import { WhiteBox } from '@/components';
import { SessionBlock } from './fragments/SessionBlock.tsx';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { Button, useBoxBreakpoints } from 'ui-kit';
import { useTranslate } from '@/i18n';
import { CONTENT_BOX_ID } from '@/features/layouts/MainLayout/MainLayout.tsx';
import { useRecaptcha } from '@/hooks/useRecaptcha.tsx';
import DATA_TEST_IDS from './dataTestIds.ts';
import {
  useInitSessions,
  useSessionActions,
  useSessions,
} from '@/store/sessions/hooks.ts';
import { ConfirmationDialog } from '@/features/profile/Security/ActiveSessions/fragments/ConfirmationDialog.tsx';
import { SessionStep } from '@/store/sessions/types.ts';
import { PinDialog } from '@/features/profile/Security/ActiveSessions/fragments/PinDialog.tsx';
import { DeviceClass } from '@/api/__generated__/webApi';

export const ActiveSessions = () => {
  const { t } = useTranslate('profile');
  useRecaptcha();
  const { lg, sm } = useBoxBreakpoints(CONTENT_BOX_ID, ['sm', 'lg']);
  useInitSessions();
  const { current, otherSessions, step, targetSession } = useSessions();
  const { openConfirmAll, openConfirmOne, requestSms, dismiss } =
    useSessionActions();

  const deviceName = targetSession?.formattedUserAgent ?? '';

  return (
    <>
      <WhiteBox sx={styles.container}>
        <Stack sx={styles.mainContent}>
          <Stack>
            <Typography sx={[styles.header, styles.ellipsis]}>
              {t('CURRENT_SESSION')}
            </Typography>
            <Stack>
              <SessionBlock
                iconType={current?.deviceClass ?? DeviceClass.UNCLASSIFIED}
                current
                title={current?.formattedUserAgent ?? t('LOADING')}
              />
            </Stack>
          </Stack>
          <Stack>
            <Typography sx={[styles.header, styles.ellipsis]}>
              {t('OTHER_SESSIONS')}
            </Typography>
            <Box
              sx={[
                styles.sessionsBlock,
                {
                  gridTemplateColumns: lg
                    ? 'repeat(3, minmax(0, 526px))'
                    : sm
                      ? 'repeat(2, minmax(0, 526px))'
                      : '1fr',
                },
              ]}
            >
              {otherSessions.length ? (
                otherSessions.map((d, i) => {
                  return (
                    <SessionBlock
                      key={i}
                      iconType={d.deviceClass}
                      onEndSession={() => {
                        openConfirmOne(d.id);
                      }}
                      subtitle={d.ipAddress}
                      title={d.formattedUserAgent}
                    />
                  );
                })
              ) : (
                <Typography sx={styles.noContent}>
                  {t('NO_OTHER_ACTIVE_SESSIONS')}
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
        <Stack gap={24} sx={styles.buttonBlock}>
          {!!otherSessions.length && (
            <Button
              data-test-id={'profile__security_active-sessions'}
              onClick={openConfirmAll}
            >
              {t('END_ALL_OTHER_SESSIONS')}
            </Button>
          )}
        </Stack>
      </WhiteBox>
      <ConfirmationDialog
        data-test-id={DATA_TEST_IDS.securityConfirmEndOneDialog}
        open={step === SessionStep.ConfirmOne}
        title={t('END_SESSION')}
        description={t('WOULD_YOU_LIKE_TO_END_SESSION_', {
          device: deviceName,
        })}
        onSubmit={requestSms}
        onClose={dismiss}
      />
      <ConfirmationDialog
        data-test-id={DATA_TEST_IDS.securityConfirmEndAllDialog}
        open={step === SessionStep.ConfirmAll}
        title={t('END_ALL_OTHER_SESSIONS')}
        description={t('WOULD_YOU_LIKE_TO_END_ALL_OTHER_SESSIONS_')}
        onSubmit={requestSms}
        onClose={dismiss}
      />
      <PinDialog />
    </>
  );
};

const styles = {
  header: {
    typography: { xs: 'h2/mobile', sm: 'h2/desktop' },
    paddingBottom: { xs: 18, sm: 24 },
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  noContent: {
    typography: { xs: 'optional/md', sm: 'optional/lg' },
    color: (t) => t.palette.content.additional.default,
  },
  sessionsBlock: {
    display: 'grid',
    gap: { xs: 18, sm: 24 },
  },
  container: {
    height: { xs: '100%', sm: 'initial', lg: 'auto' },
    flexGrow: { xs: '1', sm: 'initial', lg: '1' },
    justifyContent: { xs: 'space-between', sm: 'initial' },
  },
  mainContent: { gap: { xs: 36, sm: 60, lg: 36 }, marginBottom: 24 },
  buttonBlock: { flexDirection: { xs: 'column', sm: 'row' } },
} satisfies Record<string, SxProps<Theme>>;
